/**
 * Typed client for /admin-infra/* — backs the Infrastructure screen.
 *
 * All three endpoints return raw shapes (no envelope on this surface).
 * The legacy dashboard `useAdminInfraQuery.js` was the source of truth
 * for caching behavior; this port keeps the same polling cadences
 * (10s for Health, 60s for Components + Timeline) but moves them out
 * of the screen and into the hooks file.
 */
import { client } from './client';

export type ComponentType =
  | 'cpu-miner'
  | 'gpu-miner'
  | 'bandwidth-relay'
  | 'storage-node'
  | 'inference'
  | 'shell';

export interface InfraHealthResponse {
  sessions: {
    active: number;
    healthy: number;
    stale: number;
  };
  compute: {
    totalSpeed: number;
    totalThreads: number;
    sharesAccepted: number;
    sharesRejected: number;
    gpuMiners: number;
  };
  components: {
    enabled: number;
  };
  withdrawals: {
    pending: number;
  };
  platforms: Record<string, number>;
  sources: Record<string, number>;
  newInstalls1h: number;
  serverTime: string;
  uptimeSeconds: number;
}

export interface ComponentRow {
  _id: string;
  name: string;
  binaryName?: string;
  type: ComponentType | string;
  platform: string;
  arch?: string;
  version: string;
  algorithm?: string;
  enabled: boolean;
  activeSessions?: number;
  liveHashrate?: number;
}

export interface TimelineRow {
  hour: string; // ISO-like 'YYYY-MM-DDTHH:00:00'
  started: number;
  ended: number;
}

export async function fetchInfraHealth(): Promise<InfraHealthResponse> {
  const r = await client.get<InfraHealthResponse>('/admin-infra/health');
  return r.data;
}

export async function fetchInfraComponents(): Promise<ComponentRow[]> {
  const r = await client.get<ComponentRow[]>('/admin-infra/components');
  return r.data;
}

export async function fetchSessionsTimeline(): Promise<TimelineRow[]> {
  const r = await client.get<TimelineRow[]>('/admin-infra/sessions-timeline');
  return r.data;
}
